// src/lib/state/index.svelte.ts
// NOTE: This file requires the .svelte.ts extension because it uses $state
//
// ============================================================================
// RAW STATE USAGE RULES - DO NOT DESTRUCTURE
// ============================================================================
// $state.raw arrays ONLY update on reassignment, NOT mutation
//
// ✅ DO: domainState.alerts.items = [...items, newAlert];
// ❌ DON'T: const { items } = domainState.alerts; items.push(x);
//
// The getter/setter pattern preserves reactivity while encapsulating state.
// ============================================================================

import type { AppState, ConnectionState, AlertGroup } from '$lib/types/core/state.types';
import type { Alert } from '$lib/types/domain/alerts.types';
import { isAuthReady, isCheckingAuth } from '$lib/state/auth.state.svelte';
import { browser } from '$app/environment';
import { createLogger } from '$lib/utils/logger';
import { SvelteSet, SvelteMap } from 'svelte/reactivity';

const log = createLogger('state:alerts');

// Import notification state
export { notificationState } from './notifications.state.svelte';

// Import ML Predictions state
export {
	mlPredictionsState,
	isBatchRunning,
	hasResults,
	getRiskLevelColor,
	getRiskLevelBadgeClass,
	type MLPredictionResult,
	type BatchStatus,
	type RiskLevel
} from './mlPredictions.state.svelte';

// --- Application state ---
export const appState = $state<AppState>({
	initialized: false, // App lifecycle initialization (set by AppService)
	initializing: false,
	initStage: 'idle',
	error: null,
	lastHealthCheck: null
});

// --- Connection state ---
export const connectionState = $state<ConnectionState>({
	connected: false, // WebSocket connection status (set by WebSocketService/actions)
	connecting: false,
	reconnecting: false,
	reconnectAttempts: 0,
	error: null,
	subscriptions: new SvelteSet<string>(),
	lastMessageTime: null,
	readyForData: false // Is WS ready to send/receive application data? (set by WS Service/actions)
});

// ============================================================================
// 🚀 PERFORMANCE: RAW STATE FOR LARGE ARRAYS
// ============================================================================
// $state.raw prevents Svelte from creating deep Proxy wrappers for large arrays.
// With 4500+ approved alerts, deep proxification was causing 5+ second delays.
// These MUST be standalone variable declarations (Svelte 5 requirement).
// ============================================================================

// Alerts arrays - raw state (no deep reactivity, only triggers on reassignment)
let _alertItems = $state.raw<Alert[]>([]);
let _approvedAlertItems = $state.raw<Alert[]>([]);
let _groupedAlertItems = $state.raw<AlertGroup[]>([]);
let _groupedApprovedAlertItems = $state.raw<AlertGroup[]>([]);
let _financialData = $state.raw<any[]>([]);

// Alerts metadata - regular reactive state (small objects, need fine-grained reactivity)
type DataHealthStatus = 'fresh' | 'stale' | 'empty_cache' | 'unavailable' | 'site_data_missing' | 'unknown';
const _alertsMetadata = $state({
	isLoading: true,
	error: null as Error | null,
	lastUpdateTime: new Date(),
	initialized: false,
	dataHealth: 'fresh' as DataHealthStatus
});

// ============================================================================
// WORK ORDERS STATE
// ============================================================================
// Items array - raw state (prevents deep proxy wrappers on batch updates of ~100 items)
let _workOrderItems = $state.raw<any[]>([]);

// Work Orders metadata - regular reactive state
const _workOrdersMetadata = $state({
	isLoading: true,
	error: null as Error | null,
	lastUpdateTime: new Date(),
	initialized: false,
	updatingIds: new SvelteSet<string>()
});

// Work Orders scheduling state
const _workOrdersScheduling = $state({
	isLoading: false,
	error: null as Error | null,
	lastUpdateTime: new Date(),
	scheduledWorkOrders: new SvelteMap<string, any>(),
	weekData: {
		weekStart: null as Date | null,
		weekEnd: null as Date | null
	}
});

// ============================================================================
// AUDIT LOGS STATE
// ============================================================================
// Items array - regular state (small dataset ~100 items)
let _auditLogItems = $state<any[]>([]);

// Audit Logs metadata
const _auditLogsMetadata = $state({
	isLoading: true,
	error: null as Error | null,
	lastUpdateTime: new Date(),
	initialized: false,
	total: 0,
	count: 0,
	currentFilter: null as any
});

// ============================================================================
// --- Domain data state with getter/setter interface ---
// This maintains backward compatibility while using raw state for large arrays
// ============================================================================
export const domainState = {
	// Alerts: Uses raw state for arrays, regular state for metadata
	alerts: {
		get items() { return _alertItems; },
		set items(v: Alert[]) { _alertItems = v; },
		get approvedItems() { return _approvedAlertItems; },
		set approvedItems(v: Alert[]) { _approvedAlertItems = v; },
		get groupedItems() { return _groupedAlertItems; },
		set groupedItems(v: AlertGroup[]) { _groupedAlertItems = v; },
		get groupedApprovedItems() { return _groupedApprovedAlertItems; },
		set groupedApprovedItems(v: AlertGroup[]) { _groupedApprovedAlertItems = v; },
		get financialData() { return _financialData; },
		set financialData(v: any[]) { _financialData = v; },
		get isLoading() { return _alertsMetadata.isLoading; },
		set isLoading(v: boolean) { _alertsMetadata.isLoading = v; },
		get error() { return _alertsMetadata.error; },
		set error(v: Error | null) { _alertsMetadata.error = v; },
		get lastUpdateTime() { return _alertsMetadata.lastUpdateTime; },
		set lastUpdateTime(v: Date) { _alertsMetadata.lastUpdateTime = v; },
		get initialized() { return _alertsMetadata.initialized; },
		set initialized(v: boolean) { _alertsMetadata.initialized = v; },
		get dataHealth() { return _alertsMetadata.dataHealth; },
		set dataHealth(v: DataHealthStatus) { _alertsMetadata.dataHealth = v; }
	},
	// Work Orders: getter/setter pattern using top-level state variables
	workOrders: {
		get items() { return _workOrderItems; },
		set items(v: any[]) { _workOrderItems = v; },
		get isLoading() { return _workOrdersMetadata.isLoading; },
		set isLoading(v: boolean) { _workOrdersMetadata.isLoading = v; },
		get error() { return _workOrdersMetadata.error; },
		set error(v: Error | null) { _workOrdersMetadata.error = v; },
		get lastUpdateTime() { return _workOrdersMetadata.lastUpdateTime; },
		set lastUpdateTime(v: Date) { _workOrdersMetadata.lastUpdateTime = v; },
		get initialized() { return _workOrdersMetadata.initialized; },
		set initialized(v: boolean) { _workOrdersMetadata.initialized = v; },
		get updatingIds() { return _workOrdersMetadata.updatingIds; },
		set updatingIds(v: SvelteSet<string>) { _workOrdersMetadata.updatingIds = v; },
		scheduling: {
			get isLoading() { return _workOrdersScheduling.isLoading; },
			set isLoading(v: boolean) { _workOrdersScheduling.isLoading = v; },
			get error() { return _workOrdersScheduling.error; },
			set error(v: Error | null) { _workOrdersScheduling.error = v; },
			get lastUpdateTime() { return _workOrdersScheduling.lastUpdateTime; },
			set lastUpdateTime(v: Date) { _workOrdersScheduling.lastUpdateTime = v; },
			get scheduledWorkOrders() { return _workOrdersScheduling.scheduledWorkOrders; },
			set scheduledWorkOrders(v: SvelteMap<string, any>) { _workOrdersScheduling.scheduledWorkOrders = v; },
			get weekData() { return _workOrdersScheduling.weekData; },
			set weekData(v: { weekStart: Date | null; weekEnd: Date | null }) {
				_workOrdersScheduling.weekData = v;
			}
		}
	},
	// Audit Logs: getter/setter pattern using top-level state variables
	auditLogs: {
		get items() { return _auditLogItems; },
		set items(v: any[]) { _auditLogItems = v; },
		get isLoading() { return _auditLogsMetadata.isLoading; },
		set isLoading(v: boolean) { _auditLogsMetadata.isLoading = v; },
		get error() { return _auditLogsMetadata.error; },
		set error(v: Error | null) { _auditLogsMetadata.error = v; },
		get lastUpdateTime() { return _auditLogsMetadata.lastUpdateTime; },
		set lastUpdateTime(v: Date) { _auditLogsMetadata.lastUpdateTime = v; },
		get initialized() { return _auditLogsMetadata.initialized; },
		set initialized(v: boolean) { _auditLogsMetadata.initialized = v; },
		get total() { return _auditLogsMetadata.total; },
		set total(v: number) { _auditLogsMetadata.total = v; },
		get count() { return _auditLogsMetadata.count; },
		set count(v: number) { _auditLogsMetadata.count = v; },
		get currentFilter() { return _auditLogsMetadata.currentFilter; },
		set currentFilter(v: any) { _auditLogsMetadata.currentFilter = v; }
	}
};

// --- Internal derived values ---
// Note: These are NOT exported directly
const _isAppReady = $derived(
	browser
		? appState.initialized &&
				isAuthReady() &&
				(connectionState.connected || !connectionState.connecting)
		: false
);

const _isAppLoading = $derived(
	browser ? appState.initializing || isCheckingAuth() || connectionState.connecting : false
);

const _isDataConnectionReady = $derived(
	browser ? connectionState.connected && connectionState.readyForData : false
);

// Smart loading gate: Only show loading if actually loading AND no data exists
const _shouldShowAlertsLoading = $derived(
	browser
		? domainState.alerts.isLoading &&
		  domainState.alerts.items.length === 0 &&
		  domainState.alerts.approvedItems.length === 0
		: false
);

// --- Exported functions to access derived values ---
export function isAppReady(): boolean {
	return _isAppReady;
}

export function isAppLoading(): boolean {
	return _isAppLoading;
}

export function isDataConnectionReady(): boolean {
	return _isDataConnectionReady;
}

export function shouldShowAlertsLoading(): boolean {
	return _shouldShowAlertsLoading;
}

// --- Site Outage Overlap Cache ---
export interface SiteOutageOverlapInfo {
	site_outage_id: string;
	overlapping_alert_ids: Set<string>;
	overlapping_count: number;
	start_time: string;
	end_time: string;
}

/**
 * Helper function for case-insensitive Site Outage detection
 * Handles variations like: "Site Outage", "SITE_OUTAGE", "site_outage", "Site_Outage"
 */
export function isSiteOutage(alertType: string | undefined | null): boolean {
	if (!alertType) return false;
	const normalized = alertType.toLowerCase().replace(/[_\s]/g, '');
	return normalized === 'siteoutage';
}

/** Check if alert type is Tracker Not Configured (case-insensitive) */
export function isTrackerNotConfigured(alertType: string | undefined | null): boolean {
	if (!alertType) return false;
	const normalized = alertType.toLowerCase().replace(/[_\s]/g, '');
	return normalized === 'trackernotconfigured';
}

/** Check if alert type is Tracker Stalled (case-insensitive) */
export function isTrackerStalled(alertType: string | undefined | null): boolean {
	if (!alertType) return false;
	const normalized = alertType.toLowerCase().replace(/[_\s]/g, '');
	return normalized === 'trackerstalled';
}

// Helper function to check time overlap
function isTimeOverlap(start1: string, end1: string, start2: string, end2: string): boolean {
	const s1 = new Date(start1).getTime();
	const e1 = new Date(end1).getTime();
	const s2 = new Date(start2).getTime();
	const e2 = new Date(end2).getTime();

	return (s1 >= s2 && s1 <= e2) || (e1 >= s2 && e1 <= e2) || (s1 <= s2 && e1 >= e2);
}

/**
 * Build Site Outage overlap cache with O(n) performance
 * Extracted helper to eliminate 99 lines of duplicate code
 */
function buildOverlapCache(alerts: Alert[]): Map<string, SiteOutageOverlapInfo> {
	const cache = new Map<string, SiteOutageOverlapInfo>();

	// Index alerts by asset_id for O(n) performance instead of O(n²)
	const alertsByAsset = new Map<string, Alert[]>();
	for (const alert of alerts) {
		if (!isSiteOutage(alert.alert_type)) {
			const existing = alertsByAsset.get(alert.asset_id) || [];
			existing.push(alert);
			alertsByAsset.set(alert.asset_id, existing);
		}
	}

	// Find all Site Outage alerts (case-insensitive)
	const siteOutages = alerts.filter((a) => isSiteOutage(a.alert_type));

	for (const so of siteOutages) {
		// Skip if essential fields are missing
		if (!so.alert_flag_id || !so.start_time || !so.end_time) continue;

		const overlapping = new Set<string>();

		// Only check alerts for the same asset (O(1) lookup instead of O(n) iteration)
		const sameAssetAlerts = alertsByAsset.get(so.asset_id) || [];
		for (const alert of sameAssetAlerts) {
			if (!alert.alert_flag_id || !alert.start_time || !alert.end_time) continue;

			if (
				alert.alert_flag_id !== so.alert_flag_id &&
				isTimeOverlap(alert.start_time, alert.end_time, so.start_time, so.end_time)
			) {
				overlapping.add(alert.alert_flag_id);
			}
		}

		cache.set(so.alert_flag_id, {
			site_outage_id: so.alert_flag_id,
			overlapping_alert_ids: overlapping,
			overlapping_count: overlapping.size,
			start_time: so.start_time,
			end_time: so.end_time
		});
	}

	return cache;
}

/**
 * Build Tracker Not Configured overlap cache
 * Maps each "Tracker Not Configured" alert_flag_id to hidden "Tracker Stalled" alert count and objects
 */
function buildTrackerOverlapCache(alerts: Alert[]): Map<string, { count: number; alerts: Alert[] }> {
	const cache = new Map<string, { count: number; alerts: Alert[] }>();

	// Map component_id → Tracker Not Configured alert_flag_ids
	const notConfiguredByComponent = new Map<string, string[]>();
	for (const alert of alerts) {
		if (isTrackerNotConfigured(alert.alert_type) && alert.component_id && alert.alert_flag_id) {
			const existing = notConfiguredByComponent.get(alert.component_id) || [];
			existing.push(alert.alert_flag_id);
			notConfiguredByComponent.set(alert.component_id, existing);
		}
	}

	// Collect Tracker Stalled alerts per component (only for components that have Not Configured)
	const stalledByComponent = new Map<string, Alert[]>();
	for (const alert of alerts) {
		if (
			isTrackerStalled(alert.alert_type) &&
			alert.component_id &&
			notConfiguredByComponent.has(alert.component_id)
		) {
			const arr = stalledByComponent.get(alert.component_id) || [];
			arr.push(alert);
			stalledByComponent.set(alert.component_id, arr);
		}
	}

	// Map count + alerts to each Tracker Not Configured alert
	for (const [componentId, alertIds] of notConfiguredByComponent) {
		const stalledAlerts = stalledByComponent.get(componentId) || [];
		for (const alertId of alertIds) {
			cache.set(alertId, { count: stalledAlerts.length, alerts: stalledAlerts });
		}
	}

	console.log('[TrackerOverlap] notConfigured components:', notConfiguredByComponent.size, '| stalledByComponent:', Object.fromEntries([...stalledByComponent].map(([k, v]) => [k, v.length])), '| cache entries:', cache.size);

	return cache;
}

// Build Tracker overlap cache for regular alerts
const _trackerOverlapCache = $derived.by(() => buildTrackerOverlapCache(domainState.alerts.items));

/** Get the number of hidden Tracker Stalled alerts for a given Tracker Not Configured alert */
export function getTrackerHiddenCount(alertFlagId: string): number {
	return _trackerOverlapCache.get(alertFlagId)?.count || 0;
}

/** Get the actual hidden Tracker Stalled Alert objects for a given Tracker Not Configured alert */
export function getTrackerHiddenAlerts(alertFlagId: string): Alert[] {
	return _trackerOverlapCache.get(alertFlagId)?.alerts || [];
}

/** Get total tracker hidden count for a group (deduplicated across all Tracker Not Configured alerts) */
export function getGroupTrackerHiddenCount(alerts: Alert[]): number {
	const seenAlertIds = new Set<string>();
	for (const alert of alerts) {
		if (isTrackerNotConfigured(alert.alert_type) && alert.alert_flag_id) {
			const hidden = _trackerOverlapCache.get(alert.alert_flag_id)?.alerts || [];
			for (const h of hidden) {
				const hId = h.alert_flag_id || h.id?.toString() || '';
				if (hId) seenAlertIds.add(hId);
			}
		}
	}
	return seenAlertIds.size;
}

// Build Site Outage overlap cache for regular alerts
const _siteOutageOverlapCache = $derived.by(() => buildOverlapCache(domainState.alerts.items));

// Build Site Outage overlap cache for approved alerts
const _siteOutageOverlapCacheApproved = $derived.by(() =>
	buildOverlapCache(domainState.alerts.approvedItems)
);

// Export accessor for overlap cache (regular alerts)
export function getSiteOutageOverlapInfo(siteOutageId: string): SiteOutageOverlapInfo | undefined {
	return _siteOutageOverlapCache.get(siteOutageId);
}

// Export accessor for overlap cache (approved alerts)
export function getSiteOutageOverlapInfoApproved(
	siteOutageId: string
): SiteOutageOverlapInfo | undefined {
	return _siteOutageOverlapCacheApproved.get(siteOutageId);
}

/**
 * Build set of hidden alert IDs from a given alerts array
 * Used by AlertsHandler to filter out alerts that overlap with Site Outages
 *
 * @param alerts - Array of alerts to analyze
 * @returns Set of alert_flag_ids that should be hidden (they overlap with a Site Outage)
 */
export function buildHiddenAlertIds(alerts: Alert[]): Set<string> {
	const hiddenIds = new Set<string>();

	// Index alerts by asset for O(n) performance
	const alertsByAsset = new Map<string, Alert[]>();
	for (const alert of alerts) {
		if (!isSiteOutage(alert.alert_type)) {
			const existing = alertsByAsset.get(alert.asset_id) || [];
			existing.push(alert);
			alertsByAsset.set(alert.asset_id, existing);
		}
	}

	// Find Site Outages and collect overlapping alert IDs
	const siteOutages = alerts.filter((a) => isSiteOutage(a.alert_type));

	for (const so of siteOutages) {
		if (!so.start_time || !so.end_time) continue;

		const sameAssetAlerts = alertsByAsset.get(so.asset_id) || [];
		for (const alert of sameAssetAlerts) {
			if (!alert.start_time || !alert.end_time) continue;
			if (alert.alert_flag_id === so.alert_flag_id) continue;

			if (isTimeOverlap(alert.start_time, alert.end_time, so.start_time, so.end_time)) {
				hiddenIds.add(alert.alert_flag_id);
			}
		}
	}

	// --- Tracker Not Configured hides Tracker Stalled (same component) ---
	const notConfiguredComponents = new Set<string>();
	for (const alert of alerts) {
		if (isTrackerNotConfigured(alert.alert_type) && alert.component_id) {
			notConfiguredComponents.add(alert.component_id);
		}
	}

	for (const alert of alerts) {
		if (
			isTrackerStalled(alert.alert_type) &&
			alert.component_id &&
			notConfiguredComponents.has(alert.component_id)
		) {
			hiddenIds.add(alert.alert_flag_id);
		}
	}

	return hiddenIds;
}

// --- Grouped alerts derivations ---
// Groups alerts by asset.id for the UI
// Note: AlertGroup type is imported from '$lib/types/core/state.types' and re-exported below
export type { AlertGroup } from '$lib/types/core/state.types';

/**
 * Get grouped pending alerts by asset.id
 * Backend sends pre-grouped data with backend_filtered=true
 * Simplified: direct accessor instead of unnecessary $derived wrapper
 */
export function groupedAlerts(): AlertGroup[] {
	return domainState.alerts.groupedItems ?? [];
}

/**
 * Get grouped approved alerts by asset.id
 * Backend sends pre-grouped data with backend_filtered=true
 * Simplified: direct accessor instead of unnecessary $derived wrapper
 */
export function groupedApprovedAlerts(): AlertGroup[] {
	return domainState.alerts.groupedApprovedItems ?? [];
}

// Register state globally for debug helper (development only)
if (browser && import.meta.env.DEV) {
	(globalThis as any).__AMOS_STATE__ = {
		domainState,
		connectionState,
		appState,
		// Add debug helper
		debugAlerts: () => {
			log.debug('Alert Grouping Debug - Starting diagnostic', {
				flatItemsCount: domainState.alerts.items.length
			});

			const grouped = groupedAlerts();
			log.debug('Alert Grouping Debug - Grouped results', {
				groupedAlerts: grouped,
				groupCount: grouped.length,
				shapeValidation: {
					isArray: Array.isArray(grouped),
					hasAlertsArray: Array.isArray(grouped[0]?.alerts),
					hasAssetId: !!grouped[0]?.asset?.id
				}
			});

			// Log individual group details
			grouped.forEach((g) => {
				log.debug('Alert Grouping Debug - Group details', {
					assetId: g.asset.id,
					assetName: g.asset.name,
					alertCount: g.count,
					alerts: g.alerts.map((a) => ({
						alertFlagId: a.alert_flag_id,
						alertType: a.alert_type
					}))
				});
			});
		}
	};
}
